import { Sparkles } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { useMyLoyalty, pointsToChf } from '@/hooks/useLoyalty';
import { useTranslations } from '@/hooks/useTranslations';

interface LoyaltyRedeemBoxProps {
  /** Currently selected points to redeem */
  redeemPoints: number;
  onChange: (points: number) => void;
  /** Maximum CHF the redemption may reduce – usually subtotal after other discounts */
  maxDiscountChf: number;
}

/** Minimum balance required before customers can redeem points. */
const MIN_REDEEM_POINTS = 1000;

/** Simple opt-in box at checkout for redeeming Big-Punkte. */
export default function LoyaltyRedeemBox({ redeemPoints, onChange, maxDiscountChf }: LoyaltyRedeemBoxProps) {
  const { overview, loading } = useMyLoyalty();
  const { t } = useTranslations();

  if (loading) return null;
  if (!overview || !overview.authenticated || !overview.has_account || !overview.settings) return null;
  if (overview.balance <= 0) return null;

  const settings = overview.settings;
  const balance = overview.balance;
  const maxRedeemableByDiscount = Math.max(0, Math.floor(maxDiscountChf * settings.points_per_chf_redemption));
  const maxRedeemable = Math.min(balance, maxRedeemableByDiscount);
  const maxDiscountValue = pointsToChf(maxRedeemable, settings);
  const isActive = redeemPoints > 0;
  const currentChf = pointsToChf(redeemPoints, settings);

  const handleToggle = (checked: boolean) => {
    onChange(checked ? maxRedeemable : 0);
  };

  if (maxRedeemable < MIN_REDEEM_POINTS) {
    // Hide entirely if the customer has less than the minimum, or if maxDiscountChf
    // would not even allow the minimum redemption (avoids showing a useless toggle).
    if (balance < MIN_REDEEM_POINTS) return null;
    return (
      <div className="rounded-lg border border-dashed border-border bg-muted/30 p-3 text-xs text-muted-foreground">
        {t('loyalty.redeem_min_hint')
          .replace('{min}', MIN_REDEEM_POINTS.toLocaleString('de-CH'))
          .replace('{balance}', balance.toLocaleString('de-CH'))}
      </div>
    );
  }

  return (
    <div className={`rounded-lg border-2 p-3 transition-colors ${isActive ? 'border-primary bg-primary/5' : 'border-border bg-card'}`}>
      <div className="flex items-center gap-3">
        <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${isActive ? 'bg-primary text-primary-foreground' : 'bg-primary/10 text-primary'}`}>
          <Sparkles className="h-4 w-4" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold leading-tight">
            {t('loyalty.redeem_title')}
          </p>
          <p className="text-xs text-muted-foreground mt-0.5">
            {t('loyalty.redeem_balance')
              .replace('{balance}', balance.toLocaleString('de-CH'))
              .replace('{chf}', pointsToChf(balance, settings).toFixed(2))}
          </p>
        </div>
        <Switch
          checked={isActive}
          onCheckedChange={handleToggle}
          aria-label={t('loyalty.redeem_title')}
        />
      </div>

      {isActive ? (
        <div className="mt-2.5 flex items-center justify-between rounded-md bg-success/10 px-2.5 py-1.5 text-xs">
          <span className="font-medium text-success">
            ✓ {t('loyalty.redeem_active').replace('{points}', redeemPoints.toLocaleString('de-CH'))}
          </span>
          <span className="font-mono font-semibold text-success">
            −CHF {currentChf.toFixed(2)}
          </span>
        </div>
      ) : (
        <p className="mt-2 text-xs text-muted-foreground">
          {t('loyalty.redeem_hint')
            .replace('{points}', maxRedeemable.toLocaleString('de-CH'))
            .replace('{chf}', maxDiscountValue.toFixed(2))}
        </p>
      )}
    </div>
  );
}
